import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, Plus, LayoutDashboard, MessageSquare, User, LogOut, Search } from 'lucide-react';

export const Navbar = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-2"
            data-testid="logo-link"
          >
            <img 
              src="/cuess-logo.png" 
              alt="CUESS" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            <Link
              to="/browse"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive('/browse') 
                  ? 'bg-[#008080] text-white' 
                  : 'text-stone-600 hover:bg-stone-100'
              }`}
              data-testid="browse-link"
            >
              <Search className="w-4 h-4 inline-block mr-1.5" />
              Browse
            </Link>
            
            {isAuthenticated ? (
              <>
                <Link
                  to="/create-listing"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive('/create-listing') 
                      ? 'bg-[#008080] text-white' 
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                  data-testid="create-listing-link"
                >
                  <Plus className="w-4 h-4 inline-block mr-1.5" />
                  List Item
                </Link>
                <Link
                  to="/dashboard"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive('/dashboard') 
                      ? 'bg-[#008080] text-white' 
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                  data-testid="dashboard-link"
                >
                  <LayoutDashboard className="w-4 h-4 inline-block mr-1.5" />
                  Dashboard
                </Link>
                <Link
                  to="/messages"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive('/messages') 
                      ? 'bg-[#008080] text-white' 
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                  data-testid="messages-link"
                >
                  <MessageSquare className="w-4 h-4 inline-block mr-1.5" />
                  Messages
                </Link>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="ml-2 rounded-full w-10 h-10 p-0"
                      data-testid="user-menu-trigger"
                    >
                      {user?.profile_photo ? (
                        <img 
                          src={user.profile_photo} 
                          alt={user.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#008080] text-white flex items-center justify-center text-sm font-medium">
                          {user?.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-stone-500">{user?.location}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="rounded-full" data-testid="login-button">
                    Log In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#008080] hover:bg-[#008080]/90 rounded-full" data-testid="signup-button">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-stone-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-stone-100 animate-slideDown">
            <div className="flex flex-col space-y-2">
              <Link
                to="/browse"
                className="px-4 py-2 rounded-xl text-stone-600 hover:bg-stone-100"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-browse-link"
              >
                Browse Items
              </Link>
              
              {isAuthenticated ? (
                <>
                  <Link
                    to="/create-listing"
                    className="px-4 py-2 rounded-xl text-stone-600 hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-create-link"
                  >
                    List an Item
                  </Link>
                  <Link
                    to="/dashboard"
                    className="px-4 py-2 rounded-xl text-stone-600 hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-dashboard-link"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/messages"
                    className="px-4 py-2 rounded-xl text-stone-600 hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-messages-link"
                  >
                    Messages
                  </Link>
                  <Link
                    to="/profile"
                    className="px-4 py-2 rounded-xl text-stone-600 hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-profile-link"
                  >
                    Profile
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="px-4 py-2 rounded-xl text-red-600 hover:bg-red-50 text-left"
                    data-testid="mobile-logout-button"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-2 rounded-xl text-stone-600 hover:bg-stone-100"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-login-link"
                  >
                    Log In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 rounded-xl bg-[#008080] text-white text-center"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-signup-link"
                  >
                    Sign Up
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
